import type { PieceData, ProtoEvent, WhatIf } from '../data/types';
import { applyAlterations } from '../music/alterations';
import { applyLayer, Layer } from '../music/figuration';
import { Key, degreeToken, keyToken, parseKey } from '../music/key';
import {
  HarmonyEvent,
  Measure,
  NoteEvent,
  PieceCore,
  PunctuationSpec,
  RegionSpec,
  barAt,
  buildHarmonyEvents,
  totalLength,
} from '../music/piece';
import { midiOf, mod, parsePitch, pcNumber, transposePitch } from '../music/pitch';
import { Chord, realizeRoman } from '../music/roman';
import { TensionResult, TensionPoint, functionPull, tensionCurve } from '../music/tension';
import type { PlayNote } from '../audio/player';

/** Everything the Journey view needs about one version (original or what-if) of a piece. */
export interface PieceView {
  core: PieceCore;
  length: number;
  events: HarmonyEvent[];
  regions: RegionSpec[];
  punctuation: PunctuationSpec[];
  tension: { perEvent: TensionResult[]; points: TensionPoint[] };
}

export function buildView(piece: PieceData, whatIf?: WhatIf | null): PieceView {
  const base: PieceCore = { homeKey: piece.homeKey, measures: piece.measures, notes: piece.notes, harmony: piece.harmony, pedals: piece.pedals };
  const core = whatIf ? applyAlterations(base, whatIf.ops, whatIf.harmony ?? []) : base;
  const regions = whatIf?.regions ?? piece.regions;
  let punctuation = piece.punctuation;
  if (whatIf?.punctuation) {
    const keyOf = (p: { bar: number; at?: number }) => (p.at !== undefined ? `t${p.at}` : `b${p.bar}`);
    const over = new Map(whatIf.punctuation.map((p) => [keyOf(p), p]));
    punctuation = piece.punctuation.filter((p) => !over.has(keyOf(p)));
    for (const p of whatIf.punctuation) if (p.mark) punctuation.push({ bar: p.bar, at: p.at, mark: p.mark, label: p.label ?? '', theory: p.theory ?? '' });
    punctuation = punctuation.filter((p) => core.measures.some((m) => m.bar === p.bar)).sort((a, b) => (a.at ?? a.bar * 1000) - (b.at ?? b.bar * 1000));
  }
  const events = buildHarmonyEvents(core);
  // Chorales change key inside bars; their harmony labels carry the local key, so use it directly.
  const regionKeyOf =
    piece.kind !== 'keyboard'
      ? (_bar: number, fallback: Key) => fallback
      : (bar: number, fallback: Key) => {
          const r = regions.find((x) => bar >= x.from && bar <= x.to);
          return r ? parseKey(r.key) : fallback;
        };
  const tension = tensionCurve(events, parseKey(piece.homeKey), regionKeyOf);
  return { core, length: totalLength(core.measures), events, regions, punctuation, tension };
}

/** Map a time in one timeline to another by bar and offset (for cut variants). */
export function mapTime(from: Measure[], to: Measure[], t: number): number {
  const bar = barAt(from, t);
  const m = from.find((x) => x.bar === bar)!;
  const offset = t - m.start;
  const target = to.find((x) => x.bar === bar) ?? to.find((x) => x.bar > bar);
  if (!target) return totalLength(to);
  return target.bar === bar ? target.start + Math.min(offset, target.length) : target.start;
}

/** Deterministic "performance": bass a little louder, a light accent on the beat. */
export function toPlayNotes(notes: NoteEvent[]): PlayNote[] {
  return notes.map((n) => {
    const beat = mod(n.onset, 1) < 1e-6;
    const strong = mod(n.onset, 2) < 1e-6;
    const base = n.line === 0 ? 0.82 : n.line === 1 ? 0.7 : 0.62;
    return { onset: n.onset, dur: n.dur, midi: n.midi, line: n.line, velocity: base + (strong ? 0.1 : beat ? 0.05 : 0) };
  });
}

export function layerNotes(view: PieceView, layer: Layer): NoteEvent[] {
  return applyLayer(layer, view.core.notes, view.events, view.core.measures, view.regions);
}

export function regionAt(regions: RegionSpec[], bar: number, time?: number): RegionSpec | undefined {
  if (time !== undefined && regions.some((r) => r.start !== undefined)) {
    return regions.find((r) => time >= (r.start ?? -Infinity) - 1e-6 && time < (r.end ?? Infinity) - 1e-6) ?? regions[regions.length - 1];
  }
  return regions.find((r) => bar >= r.from && bar <= r.to);
}

export function eventAt(events: HarmonyEvent[], t: number): HarmonyEvent | undefined {
  for (let i = events.length - 1; i >= 0; i--) if (t >= events[i].start - 1e-9) return events[i];
  return events[0];
}

/* ---------- Path prototypes ---------- */

export function protoLength(events: ProtoEvent[]): number {
  return events.reduce((a, e) => a + e.dur, 0);
}

export type VoiceFilter = 'all' | 'outer' | 'bass' | 'melody';

/** Turn a path prototype into playable notes, optionally transposed or thinned to some voices. */
export function protoNotes(events: ProtoEvent[], opts: { steps?: number; semis?: number; offset?: number; voices?: VoiceFilter } = {}): PlayNote[] {
  const out: PlayNote[] = [];
  let t = opts.offset ?? 0;
  for (const ev of events) {
    const pitches = ev.notes.map((n) => transposePitch(parsePitch(n), opts.steps ?? 0, opts.semis ?? 0)).map(midiOf).sort((a, b) => a - b);
    pitches.forEach((midi, i) => {
      const top = i === pitches.length - 1;
      const keep =
        opts.voices === 'outer' ? i === 0 || top : opts.voices === 'bass' ? i === 0 : opts.voices === 'melody' ? top : true;
      if (keep) out.push({ onset: t, dur: ev.dur, midi, line: i, velocity: i === 0 ? 0.8 : top ? 0.78 : 0.62 });
    });
    t += ev.dur;
  }
  return out;
}

export function transposeKeyLabel(key: string, steps: number, semis: number): string {
  const k = parseKey(key);
  const p = transposePitch({ ...k.tonic, octave: 4 }, steps, semis);
  return keyToken({ tonic: { letter: p.letter, alter: p.alter }, mode: k.mode });
}

/** Bass contour of a prototype in diatonic steps above the tonic (0 = the tonic at or below the first bass note). */
export function bassContour(events: ProtoEvent[], keyToken: string): Array<{ step: number; degree: string }> {
  if (!events.length) return [];
  const key = parseKey(keyToken);
  const basses = events.map((e) => e.notes.map(parsePitch).sort((a, b) => midiOf(a) - midiOf(b))[0]);
  const firstIndex = basses[0].letter + 7 * basses[0].octave;
  let tonicIndex = key.tonic.letter + 7 * basses[0].octave;
  if (tonicIndex > firstIndex) tonicIndex -= 7;
  return basses.map((b) => ({ step: b.letter + 7 * b.octave - tonicIndex, degree: degreeToken(b, key) }));
}

/* ---------- Terrain geometry ---------- */

const LETTER_FIFTHS = [0, 2, 4, -1, 1, 3, 5];

/** Distance from the centre of the well for a given pull (0–1). Home sits alone at the centre. */
export function wellRadius(pull: number): number {
  return 0.3 + 0.68 * Math.min(1, Math.max(0, pull));
}

/** Position of a chord in the gravity well of a key: angle by circle of fifths, radius by pull. */
export function terrainPosition(chord: Chord, key: Key): { angle: number; radius: number } {
  const rootF = LETTER_FIFTHS[chord.root.letter] + 7 * chord.root.alter;
  const tonicF = LETTER_FIFTHS[key.tonic.letter] + 7 * key.tonic.alter + (key.mode === 'minor' ? 0 : 0);
  const diff = rootF - tonicF;
  // Dominant side to the right, subdominant side to the left; tonic at the top.
  const angle = -90 + diff * 30;
  const pull = chord.cadential ? 0.75 : functionPull(chord);
  return { angle, radius: wellRadius(pull) };
}

export interface TerrainNode {
  symbol: string;
  chord: Chord;
  x: number;
  y: number;
  radius: number;
}

/** Lay out chords in a unit well, nudging nodes apart when they would overlap. */
export function layoutTerrain(symbols: string[], key: Key): TerrainNode[] {
  const nodes: TerrainNode[] = [];
  for (const symbol of symbols) {
    const chord = realizeRoman(symbol, key);
    const isTonic = chord.fn === 'T' && pcNumber(chord.root) === pcNumber(key.tonic) && !chord.applied && !chord.cadential;
    let { angle, radius } = terrainPosition(chord, key);
    if (isTonic) radius = 0;
    let x = radius * Math.cos((angle * Math.PI) / 180);
    let y = radius * Math.sin((angle * Math.PI) / 180);
    for (let tries = 0; tries < 12; tries++) {
      const clash = nodes.find((n) => Math.hypot(n.x - x, n.y - y) < 0.19);
      if (!clash) break;
      angle += tries % 2 === 0 ? 16 * (tries + 1) : -16 * (tries + 1);
      x = radius * Math.cos((angle * Math.PI) / 180);
      y = radius * Math.sin((angle * Math.PI) / 180);
    }
    nodes.push({ symbol, chord, x, y, radius });
  }
  return nodes;
}

export function keyLabel(key: Key): string {
  return keyToken(key);
}
